import { useEffect, useRef, useState } from "react";
import { X, ArrowUp, Compass } from "lucide-react";
import { ICON, fmtDist, fmtDur, eta } from "./Directions";
import { aheadPoint, bearing } from "./useNavigation";

function useCamera(vid) {
  const [err, setErr] = useState("");
  useEffect(() => {
    let stream = null, dead = false;
    if (!navigator.mediaDevices?.getUserMedia) { setErr("Camera is not available in this browser."); return undefined; }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false })
      .then((s) => {
        if (dead) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        if (vid.current) { vid.current.srcObject = s; vid.current.play().catch(() => {}); }
      })
      .catch((e) => setErr(e?.name === "NotAllowedError" ? "Camera permission denied. Allow camera access for this site in your browser settings." : "Camera is not available on this device."));
    return () => { dead = true; stream?.getTracks().forEach((t) => t.stop()); };
  }, [vid]);
  return err;
}

export default function LiveView({ nav, compass, onClose }) {
  const vid = useRef(null);
  const err = useCamera(vid);
  const { route, progress, pos } = nav;
  const step = route && progress ? route.steps[progress.next] : null;
  const facing = compass ?? pos?.heading ?? null;
  const target = route && pos ? aheadPoint([pos.lat, pos.lon], route.coords, Math.min(80, Math.max(25, progress?.to_next_m || 80))) : null;
  const rel = target && facing != null ? bearing([pos.lat, pos.lon], target) - facing : null;
  const I = step ? ICON[step.dir] || ArrowUp : ArrowUp;
  const kmh = pos?.speed != null ? Math.round(pos.speed * 3.6) : null;
  return (
    <div className="fixed inset-0 z-[2000] bg-black text-white" data-testid="drv-liveview">
      <video ref={vid} playsInline muted autoPlay className="absolute inset-0 h-full w-full object-cover" data-testid="drv-liveview-video" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/60" />
      <div className="absolute inset-x-3 top-[max(12px,env(safe-area-inset-top))] flex items-center gap-3 rounded-2xl bg-[#0A234A]/90 px-4 py-3 shadow-xl backdrop-blur" data-testid="drv-liveview-step">
        <I className="h-10 w-10 flex-shrink-0 text-[#FF5722]" />
        <div className="min-w-0 flex-1">
          <p className="font-mono text-2xl font-bold leading-none">{progress ? fmtDist(progress.to_next_m) : "—"}</p>
          <p className="mt-1 text-sm font-semibold leading-tight">{step ? step.text : "Waiting for route…"}</p>
        </div>
        <button data-testid="drv-liveview-close" onClick={onClose} className="rounded-full bg-white/15 p-2 hover:bg-white/25" aria-label="Close live view"><X className="h-5 w-5" /></button>
      </div>
      <div className="absolute inset-x-0 top-[38%] flex justify-center" style={{ perspective: "700px" }}>
        {rel != null ? (
          <div className="transition-transform duration-500 ease-out" style={{ transform: `rotateX(58deg) rotateZ(${rel}deg)` }} data-testid="drv-liveview-arrow">
            <svg viewBox="0 0 120 160" width="150" height="200"><path d="M60 4 L114 84 L78 74 L78 156 L42 156 L42 74 L6 84 Z" fill="#FF5722" fillOpacity="0.92" stroke="#fff" strokeWidth="5" strokeLinejoin="round" /></svg>
          </div>
        ) : (
          <p className="mx-6 flex items-center gap-2 rounded-xl bg-black/60 px-4 py-3 text-sm font-semibold" data-testid="drv-liveview-calibrate"><Compass className="h-5 w-5 text-[#FF5722]" />{pos ? "Finding your heading. Hold the phone upright, or start moving." : "Waiting for GPS…"}</p>
        )}
      </div>
      {err && <p className="absolute inset-x-6 top-[60%] rounded-xl bg-red-600/90 px-4 py-3 text-center text-sm font-semibold" data-testid="drv-liveview-error">{err}</p>}
      <div className="absolute inset-x-3 bottom-[max(12px,env(safe-area-inset-bottom))] rounded-2xl bg-white/95 px-4 py-3 text-[#0A234A] shadow-xl" data-testid="drv-liveview-summary">
        <div className="flex items-center gap-3">
          {kmh != null && <span className="flex h-12 w-12 flex-col items-center justify-center rounded-full border-2 border-[#0A234A] font-mono leading-none" data-testid="drv-liveview-speed"><b className="text-base">{kmh}</b><span className="text-[8px]">km/h</span></span>}
          {progress && <p className="font-mono text-xs text-slate-600"><span className="text-xl font-bold text-emerald-700">{fmtDur(progress.left_s)}</span> · {fmtDist(progress.left_m)} · {eta(progress.left_s)}</p>}
        </div>
        <p className="mt-2 text-[10px] leading-snug text-slate-500">Live view is for getting your bearings. Keep your eyes on the road and mount the phone. Never hold it while driving.</p>
      </div>
    </div>
  );
}
